import React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import useNavigation from './hooks/useNavigation';
import Sidebar from './components/Sidebar';
import './MainLayout.css';

/**
 * MainLayout Component
 * 
 * Provides the primary structural wrapper for the application.
 * It integrates the global Apple-inspired Sidebar and a Unified Header.
 * This layout remains mounted during navigation between protected routes
 * to preserve state (like sidebar expansion).
 * 
 * @component
 * @returns {JSX.Element} The rendered layout structure with Sidebar, Header, and Page Content.
 */
const MainLayout = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const { 
        isSidebarExpanded, 
        isSidebarLocked,
        toggleSidebar, 
        toggleSidebarLock,
        closeSidebar,
        getPageTitle,
        handleBack
    } = useNavigation();

    const getRoleLabel = (role) => {
        switch (role) {
            case 'admin': return 'Admin';
            case 'manager': return 'Manager';
            case 'employee': return 'Mitarbeiter';
            default: return role;
        }
    };

    return (
        <div className={`main-layout ${isSidebarLocked ? 'sidebar-locked' : isSidebarExpanded ? 'sidebar-expanded' : 'sidebar-collapsed'}`}>
            {/* Sidebar Component */}
            <Sidebar 
                isExpanded={isSidebarExpanded} 
                isLocked={isSidebarLocked}
                onToggle={toggleSidebar} 
                onToggleLock={toggleSidebarLock}
                onClose={closeSidebar}
                user={user}
            />

            <div className="layout-container">
                {/* Unified Header */}
                <header className="unified-header">
                    <div className="header-left">
                        <button className="global-back-button" onClick={handleBack} title="Zurück">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5M12 19l-7-7 7-7"/>
                            </svg>
                            <span>Zurück</span>
                        </button>

                        <h1 className="page-title">{getPageTitle()}</h1>
                    </div>

                    <div className="header-right">
                        <div className="user-profile">
                            <div className="user-info">
                                <span className="username">{user?.username}</span>
                                <span className="user-role">{getRoleLabel(user?.role)}</span>
                            </div>
                            <button className="logout-btn" onClick={logout}>
                                Logout
                            </button>
                        </div>
                    </div>
                </header>

                {/* Main Content Area */}
                <main className="layout-content">
                    <Outlet />
                </main>
            </div>
        </div>
    );
};

export default MainLayout;
